/**
 * PupRing Custom JavaScript - Bundle Builder State Module
 * ========================================================
 * Buy 3 for $99 Bundle Builder - State Management
 *
 * Features:
 * - BundleStateManager class with singleton pattern
 * - Memory-first state with debounced localStorage persistence
 * - Non-blocking initialization via requestIdleCallback
 * - Navigation guards and step validation
 * - Subscription system for reactive UI updates
 * - Theme editor compatible (cleanup/reinit)
 *
 * Dependencies: None (standalone module - must load first)
 *
 * Pattern Reference: BOGOUtilsAdapter in custom-bogo.js
 */

/**
 * BundleStateManager - State management for Buy 3 for $99 Bundle Builder
 *
 * Design Principles:
 * 1. Memory-first reads (microseconds, no I/O blocking)
 * 2. Debounced writes to localStorage (300ms delay)
 * 3. 24-hour expiry on persisted state
 * 4. Navigation guards prevent invalid step transitions
 * 5. Subscription system for reactive UI updates
 */
class BundleStateManager {
  // ═══════════════════════════════════════════════════════════════════════════
  // CONFIGURATION CONSTANTS
  // ═══════════════════════════════════════════════════════════════════════════

  static STORAGE_KEY = 'pupring-bundle-3for99-v1';
  static SAVE_DELAY = 300;      // ms debounce for localStorage writes
  static EXPIRY_HOURS = 24;     // State expires after 24 hours
  static VERSION = 1;           // For future state migrations

  // ═══════════════════════════════════════════════════════════════════════════
  // STEP REQUIREMENTS (Navigation Guards)
  // ═══════════════════════════════════════════════════════════════════════════

  static STEP_REQUIREMENTS = {
    'selection': () => true,
    'choice': (state) => state.products.length === 3,
    'together': (state) =>
      state.products.length === 3 &&
      state.customizationMode === 'together' &&
      state.hasCommonVariants,
    'individual-1': (state) =>
      state.products.length === 3 &&
      state.customizationMode === 'individual',
    'individual-2': (state) =>
      state.customizationMode === 'individual' &&
      BundleStateManager.isProductComplete(state.products[0]),
    'individual-3': (state) =>
      state.customizationMode === 'individual' &&
      BundleStateManager.isProductComplete(state.products[1]),
    'upsells': (state) =>
      BundleStateManager.areAllProductsComplete(state),
    // BUG-080 FIX: Add validation to summary step (defense in depth)
    'summary': (state) => BundleStateManager.areAllProductsComplete(state)
  };

  static STEP_BACK_MAP = {
    'choice': 'selection',
    'together': 'selection',      // BUG-067 FIX: Back goes to selection, not choice modal
    'individual-1': 'selection',  // BUG-067 FIX: Back goes to selection, not choice modal
    'individual-2': 'individual-1',
    'individual-3': 'individual-2',
    'upsells': null,  // Determined dynamically by customizationMode
    'summary': 'upsells'
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // CONSTRUCTOR
  // ═══════════════════════════════════════════════════════════════════════════

  constructor() {
    this.state = null;           // In-memory state (source of truth)
    this.saveTimer = null;       // Debounce timer for localStorage
    this.isHydrated = false;     // Has initial load completed?
    this.listeners = new Set();  // UI update callbacks
    this.boundFlush = null;      // Bound beforeunload handler for cleanup
    this.boundVisibilityChange = null;  // Bound visibilitychange handler for cleanup

    // BUG-062 FIX: Track localStorage corruption for user notification
    this.wasCorrupted = false;
    this.corruptionReason = null;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SINGLETON PATTERN
  // ═══════════════════════════════════════════════════════════════════════════

  static getInstance() {
    if (!window._bundleStateManager) {
      window._bundleStateManager = new BundleStateManager();
    }
    return window._bundleStateManager;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // INITIALIZATION (Non-Blocking)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Initialize state manager with non-blocking localStorage hydration
   * Uses requestIdleCallback with 100ms timeout fallback
   * @returns {Promise<Object>} The initialized state
   */
  async init() {
    // Already hydrated? Return current state
    if (this.isHydrated) {
      return this.state;
    }

    return new Promise((resolve) => {
      const hydrateState = () => {
        try {
          // Attempt to load from localStorage
          const stored = localStorage.getItem(BundleStateManager.STORAGE_KEY);

          if (stored) {
            const parsed = JSON.parse(stored);

            // Version check
            if (parsed.version !== BundleStateManager.VERSION) {
              // Future: migrate state here
              this.state = this.getInitialState();
            }
            // Expiry check
            else if (this.isStateExpired(parsed)) {
              this.state = this.getInitialState();
            }
            // Valid state - use it
            else {
              this.state = parsed;
              // A gift box quantity without a box type cannot be added to the cart
              if (parsed.upsells?.giftBoxQuantity > 0 && !parsed.upsells.giftBoxType) {
                parsed.upsells.giftBoxQuantity = 0;
              }
            }
          } else {
            // No stored state - start fresh
            this.state = this.getInitialState();
          }
        } catch (e) {
          // JSON parse error or localStorage disabled - start fresh
          this.state = this.getInitialState();

          // BUG-062 FIX: Track corruption for user notification
          this.wasCorrupted = true;
          this.corruptionReason = this.categorizeStorageError(e);

          // Try to clean up the corrupted entry to prevent repeat errors
          try {
            localStorage.removeItem(BundleStateManager.STORAGE_KEY);
          } catch (cleanupError) {
            // Ignore - might fail if localStorage completely disabled
          }

          // Dispatch event for any existing listeners (defensive)
          window.dispatchEvent(new CustomEvent('bundle:state-corrupted', {
            detail: { reason: this.corruptionReason, error: e.message }
          }));
        }

        // Mark as hydrated
        this.isHydrated = true;

        // Setup flush handlers for page unload
        this.setupFlushHandlers();

        // Resolve with the state
        resolve(this.state);
      };

      // Non-blocking: Use idle callback with timeout fallback
      if ('requestIdleCallback' in window) {
        requestIdleCallback(hydrateState, { timeout: 100 });
      } else {
        // Fallback for browsers without requestIdleCallback
        setTimeout(hydrateState, 0);
      }
    });
  }

  /**
   * Validate restored state - remove products that no longer exist
   * Called during init after localStorage hydration
   *
   * Enhancements (Phase 9):
   * - Uses fetchWithTimeout to prevent hangs on slow networks (10s for validation)
   * - Dispatches bundle:variant-oos event when variant is OOS for UI modal
   *
   * BUG-057 FIX: PARALLEL validation using Promise.allSettled
   * - Before: Sequential loop = 3 products × 10s timeout = 30s max
   * - After: Parallel fetches = ~10s max (limited by slowest single request)
   * - Performance improvement: 3x faster in worst-case scenarios
   *
   * @returns {Promise<void>}
   */
  async validateAndRestoreState() {
    if (!this.state.products || this.state.products.length === 0) {
      return; // Nothing to validate
    }

    const validProducts = [];
    const invalidProducts = [];
    const oosVariants = []; // Track OOS variants for UI notification

    // Results are per entry: the same product can fill several positions with different
    // variants, so entries are matched on product AND variant
    const entryKey = p => `${p.productId}:${p.selectedVariantId}`;
    const oosKeys = new Set();

    // BUG-057 FIX: PARALLEL validation - all products fetch simultaneously
    // Before: Sequential loop = 3 products x 10s = 30s worst case
    // After: Promise.allSettled = ~10s max (limited by slowest single request)
    const validationPromises = this.state.products.map((product, idx) =>
      this.validateSingleProduct(product, idx)
    );

    // Wait for ALL validations to complete (success or failure)
    const results = await Promise.allSettled(validationPromises);

    // Process results in order - maintains original product sequence
    results.forEach((result) => {
      if (result.status === 'fulfilled') {
        const { product, originalIndex, status, clearedProduct } = result.value;

        switch (status) {
          case 'valid':
            // Variant exists and available - keep as-is
            validProducts.push(product);
            break;

          case 'oos':
            // Variant exists but OOS - keep product with cleared variant
            validProducts.push(clearedProduct);
            oosKeys.add(entryKey(product));
            // Track OOS variant for modal
            oosVariants.push({
              productIndex: originalIndex,
              productTitle: product.productTitle,
              variantTitle: product.selectedVariantTitle,
              productId: product.productId
            });
            break;

          case 'invalid':
            // Product/variant no longer exists
            invalidProducts.push(product);
            break;

          case 'error':
            // Network error or timeout - keep product (don't block user)
            validProducts.push(product);
            break;
        }
      } else {
        // Unexpected Promise rejection - keep product (defensive)
        const idx = result.reason?.originalIndex ?? 0;
        const product = this.state.products[idx];
        if (product) {
          validProducts.push(product);
        }
      }
    });

    // Update state if validation found issues
    if (invalidProducts.length > 0 || oosVariants.length > 0) {
      // BUG-059 FIX: Reconciliation-based update
      // Instead of using validProducts (from original snapshot), apply changes to CURRENT state
      // This preserves user's modifications (removals/additions) made during validation

      // Step 1: Get CURRENT products (may have been modified by user during validation)
      const currentProducts = this.state.products;

      // Step 2: Identify entries validation wants to REMOVE (invalid = not found in system)
      const invalidKeys = new Set(invalidProducts.map(entryKey));

      // Step 3: Filter CURRENT products - only remove the entries validation found invalid
      // User removals: already reflected in currentProducts (not affected)
      // Validation removals: applied here
      let reconciledProducts = currentProducts.filter(p => !invalidKeys.has(entryKey(p)));

      // Step 4: Clear only the entries whose variant went OOS. An entry whose variant the
      // user changed during validation no longer matches and keeps the new selection.
      reconciledProducts = reconciledProducts.map(currentProduct => {
        if (oosKeys.has(entryKey(currentProduct))) {
          return {
            ...currentProduct,
            selectedVariantId: null,
            selectedVariantTitle: null,
            selectedVariantImage: currentProduct.featuredImage,
            selectedOptions: {}
          };
        }
        return currentProduct;
      });

      // Build update object using reconciled products (BUG-056 pattern preserved)
      const updates = { products: reconciledProducts };

      // If less than 3 products remain, reset customization state
      // This mirrors the guard logic in removeProduct()
      if (reconciledProducts.length < 3) {
        updates.customizationMode = null;
        updates.hasCommonVariants = false;
        updates.commonVariants = null;
        updates.unifiedCustomization = null;

        // Reset to selection step if currently on a step that requires 3 products
        if (this.state.currentStep !== 'selection') {
          updates.currentStep = 'selection';
        }
      }

      // Single atomic state update with reconciled data
      this.set(updates);

      // Dispatch step-changed event if step was reset (for UI router to update visibility)
      if (updates.currentStep === 'selection') {
        window.dispatchEvent(new CustomEvent('bundle:step-changed', {
          detail: { step: 'selection', state: this.state }
        }));
      }
    }

    // Dispatch events for UI updates

    // Products completely removed (no longer exist)
    if (invalidProducts.length > 0) {
      window.dispatchEvent(new CustomEvent('bundle:products-invalidated', {
        detail: { removed: invalidProducts.length }
      }));
    }

    // Variants that went OOS - show modal for user to re-select
    // Dispatch one event per OOS variant for sequential handling
    for (const oos of oosVariants) {
      window.dispatchEvent(new CustomEvent('bundle:variant-oos', {
        detail: oos
      }));
    }
  }

  /**
   * Validate a single product - helper for parallel validation
   * Isolated to allow Promise.allSettled to process each independently
   *
   * BUG-057 FIX: Extracted from sequential loop for parallel execution
   *
   * @param {Object} product - Product object from state
   * @param {number} originalIndex - Original index in products array (for OOS modal)
   * @returns {Promise<Object>} Validation result with status and data
   */
  async validateSingleProduct(product, originalIndex) {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await BundleStateManager.fetchWithTimeout(
        `${root}products/${encodeURIComponent(product.productHandle)}.js`,
        {},
        10000 // 10s timeout per product
      );

      if (response.status === 404) {
        // Product no longer exists
        return { product, originalIndex, status: 'invalid' };
      }

      if (!response.ok) {
        // Rate limit or server error - product is unverified, keep it as-is
        return { product, originalIndex, status: 'error' };
      }

      const productData = await response.json();

      if (!product.selectedVariantId) {
        // Variant was cleared earlier (sold out) - keep the entry so the customer can pick another
        return { product, originalIndex, status: 'valid' };
      }

      // Check if selected variant still exists and is available
      const variant = productData.variants.find(
        v => v.id === product.selectedVariantId
      );

      if (variant && variant.available) {
        // Variant exists and available - valid
        return { product, originalIndex, status: 'valid' };
      } else if (variant) {
        // Variant exists but OOS - clear variant selection
        return {
          product,
          originalIndex,
          status: 'oos',
          clearedProduct: {
            ...product,
            selectedVariantId: null,
            selectedVariantTitle: '',
            selectedVariantImage: ''
          }
        };
      } else {
        // Variant doesn't exist at all - product may have changed
        return { product, originalIndex, status: 'invalid' };
      }

    } catch (e) {
      // Network error or timeout - keep product, will validate again later
      return { product, originalIndex, status: 'error' };
    }
  }

  /**
   * Check if stored state has expired (older than EXPIRY_HOURS)
   * @param {Object} state - The state to check
   * @returns {boolean} True if expired
   */
  isStateExpired(state) {
    if (!state.updatedAt) return true;

    const hoursSinceUpdate = (Date.now() - state.updatedAt) / (1000 * 60 * 60);
    return hoursSinceUpdate >= BundleStateManager.EXPIRY_HOURS;
  }

  /**
   * Setup beforeunload and visibilitychange handlers to flush pending writes
   */
  setupFlushHandlers() {
    // Bind once and store for cleanup
    this.boundFlush = () => this.flushPendingWrites();
    this.boundVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        this.flushPendingWrites();
      }
    };

    // Flush on page unload
    window.addEventListener('beforeunload', this.boundFlush);

    // Flush when tab becomes hidden (mobile browsers may not fire beforeunload)
    document.addEventListener('visibilitychange', this.boundVisibilityChange);
  }

  /**
   * Categorize storage error for user messaging
   * BUG-062 FIX: Provides context for toast message
   * @param {Error} e - The caught error
   * @returns {string} Error category
   */
  categorizeStorageError(e) {
    const message = e.message?.toLowerCase() || '';
    const name = e.name || '';

    if (name === 'SyntaxError' || message.includes('json') || message.includes('parse')) {
      return 'data_corrupted';
    }
    if (name === 'QuotaExceededError' || message.includes('quota')) {
      return 'storage_full';
    }
    if (name === 'SecurityError' || message.includes('security')) {
      return 'storage_blocked';
    }
    return 'unknown';
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // INITIAL STATE FACTORY
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Generate a fresh initial state with unique ID
   * @returns {Object} The initial state structure
   */
  getInitialState() {
    const now = Date.now();
    const randomId = Math.random().toString(36).substring(2, 11);

    return {
      // Meta
      id: `bundle_${now}_${randomId}`,
      version: BundleStateManager.VERSION,
      createdAt: now,
      updatedAt: now,

      // Navigation
      currentStep: 'selection',

      // Products (max 3)
      products: [],

      // Customization mode
      customizationMode: null,  // 'together' | 'individual' | null
      hasCommonVariants: false,
      commonVariants: null,

      // Unified customization (together mode only)
      unifiedCustomization: null,

      // Upsells
      upsells: {
        giftBoxQuantity: 0,     // Set to 3 (one per product) when a box is selected
        giftBoxType: null,      // Set when the customer selects a box
        giftNote: '',           // Up to 350 characters
        hasWarranty: false
      }
    };
  }

  /**
   * Create a new product entry with default values
   * @param {Object} data - Product data
   * @returns {Object} Complete product structure
   */
  createProductEntry(data) {
    // Accept pre-filled customization data (from quick-view modal) or use defaults
    const prefilledCustomization = data.customization || {};

    return {
      // Identification
      productId: data.productId || null,
      productHandle: data.productHandle || '',
      productTitle: data.productTitle || '',
      featuredImage: data.featuredImage || '',

      // Variant selection
      selectedVariantId: data.selectedVariantId || null,
      selectedVariantTitle: data.selectedVariantTitle || '',
      selectedVariantImage: data.selectedVariantImage || '',
      selectedOptions: data.selectedOptions || {},

      // Product config (from variant)
      pendantCount: data.pendantCount || 1,
      pendantSize: data.pendantSize || '1.5 cm',

      // Customization data (can be pre-filled from quick-view modal)
      customization: {
        petNames: prefilledCustomization.petNames || [],
        photoUrl: prefilledCustomization.photoUrl || null,
        engraving: prefilledCustomization.engraving || {
          1: { lines: [], hasText: false },
          2: { lines: [], hasText: false },
          3: { lines: [], hasText: false },
          4: { lines: [], hasText: false }
        }
      },

      // Requirements (from product tags)
      requirements: {
        charLimit: data.charLimit || 8,
        requiresPhoto: data.requiresPhoto || false,
        requiresPetName: data.requiresPetName || false,
        noCaps: data.noCaps || false
      }
    };
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // STATE ACCESS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Get current state (memory-first, microseconds)
   * @returns {Object} Current state
   */
  get() {
    return this.state || this.getInitialState();
  }

  /**
   * Update state with partial updates
   * @param {Object} updates - Partial state updates to merge
   */
  set(updates) {
    // Merge updates into state
    this.state = {
      ...this.state,
      ...updates,
      updatedAt: Date.now()
    };

    // Notify listeners immediately (memory is updated)
    this.notifyListeners();

    // Debounce localStorage write
    this.debouncedSave();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SUBSCRIPTION SYSTEM
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Subscribe to state changes
   * @param {Function} listener - Callback function receiving state
   * @returns {Function} Unsubscribe function
   */
  subscribe(listener) {
    if (typeof listener !== 'function') {
      return () => {};
    }

    this.listeners.add(listener);

    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Notify all listeners of state change
   * Each listener is wrapped in try/catch to prevent one error from breaking all
   */
  notifyListeners() {
    this.listeners.forEach((fn) => {
      try {
        fn(this.state);
      } catch (e) {
        // Continue to next listener even if one throws
      }
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // NAVIGATION
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Check if navigation to a step is allowed
   * @param {string} step - Target step name
   * @returns {boolean} True if navigation is allowed
   */
  canNavigateTo(step) {
    const requirement = BundleStateManager.STEP_REQUIREMENTS[step];
    if (!requirement) {
      // Invalid step name
      return false;
    }
    return requirement(this.state);
  }

  /**
   * BUG-048 FIX: Diagnose why navigation to a step is blocked
   * Returns specific reason and recovery step instead of generic "Session expired"
   *
   * @param {string} step - Target step name
   * @returns {Object} { allowed: boolean, reason: string|null, recoveryStep: string|null }
   */
  diagnoseStepAccess(step) {
    // 1. Check if step is valid
    if (!BundleStateManager.STEP_REQUIREMENTS[step]) {
      return { allowed: false, reason: 'Invalid navigation. Please restart your bundle.', recoveryStep: 'selection' };
    }

    // 2. Check products count first (most common failure after validation)
    const stepsRequiring3Products = ['choice', 'together', 'individual-1', 'individual-2', 'individual-3', 'upsells'];
    if (stepsRequiring3Products.includes(step) && this.state.products.length < 3) {
      const missing = 3 - this.state.products.length;
      return {
        allowed: false,
        reason: `Please select ${missing} more product${missing > 1 ? 's' : ''} to continue.`,
        recoveryStep: 'selection'
      };
    }

    // 3. Check Together mode requirements
    if (step === 'together') {
      if (this.state.customizationMode !== 'together') {
        return {
          allowed: false,
          reason: 'Please select "Customize Together" to continue.',
          recoveryStep: this.state.products.length === 3 ? 'choice' : 'selection'
        };
      }
      if (!this.state.hasCommonVariants) {
        return {
          allowed: false,
          reason: 'Your products have different options. Please customize individually.',
          recoveryStep: 'choice'
        };
      }
    }

    // 4. Check Individual mode requirements
    if (step.startsWith('individual-') && this.state.customizationMode !== 'individual') {
      return {
        allowed: false,
        reason: 'Please select "Customize Individually" to continue.',
        recoveryStep: this.state.products.length === 3 ? 'choice' : 'selection'
      };
    }

    // 5. Check product completion for individual-2 (requires Product 1 complete)
    if (step === 'individual-2') {
      const product0 = this.state.products[0];
      if (product0 && !BundleStateManager.isProductComplete(product0)) {
        return this.diagnoseProductIncomplete(product0, 0, 'individual-1');
      }
    }

    // 6. Check product completion for individual-3 (requires Product 2 complete)
    if (step === 'individual-3') {
      const product1 = this.state.products[1];
      if (product1 && !BundleStateManager.isProductComplete(product1)) {
        return this.diagnoseProductIncomplete(product1, 1, 'individual-2');
      }
    }

    // 7. Check upsells step (requires all products complete)
    if (step === 'upsells') {
      // Handle Together mode separately (data in unifiedCustomization)
      if (this.state.customizationMode === 'together') {
        return this.diagnoseTogetherModeComplete();
      }

      // Individual mode: check each product
      const firstIncomplete = this.findFirstIncompleteProduct();
      if (firstIncomplete !== null) {
        return this.diagnoseProductIncomplete(
          this.state.products[firstIncomplete],
          firstIncomplete,
          `individual-${firstIncomplete + 1}`
        );
      }
    }

    // 8. All checks passed
    return { allowed: true, reason: null, recoveryStep: null };
  }

  /**
   * BUG-048 FIX: Diagnose Together mode completion
   * Checks unifiedCustomization for pet names and photo
   * @returns {Object} Diagnosis result
   */
  diagnoseTogetherModeComplete() {
    // Check all products have variants
    for (let i = 0; i < this.state.products.length; i++) {
      if (!this.state.products[i].selectedVariantId) {
        return {
          allowed: false,
          reason: 'Please select options for your bundle.',
          recoveryStep: 'together'
        };
      }
    }

    // Check pet name requirements (Together mode stores in unifiedCustomization)
    const maxPendants = BundleStateManager.getTogetherNameCount(this.state.products);
    if (maxPendants > 0) {
      const petNames = this.state.unifiedCustomization?.petNames || [];

      for (let i = 0; i < maxPendants; i++) {
        if (!petNames[i] || petNames[i].trim() === '') {
          return {
            allowed: false,
            reason: maxPendants > 1 ? `Please add a name for pendant ${i + 1}.` : "Please add your pet's name.",
            recoveryStep: 'together'
          };
        }
      }
    }

    // Check photo requirement (Together mode stores in unifiedCustomization)
    const anyRequiresPhoto = this.state.products.some(p => p.requirements?.requiresPhoto);
    if (anyRequiresPhoto && !this.state.unifiedCustomization?.photoUrl) {
      return {
        allowed: false,
        reason: 'Please upload a photo for your bundle.',
        recoveryStep: 'together'
      };
    }

    return { allowed: true, reason: null, recoveryStep: null };
  }

  /**
   * BUG-048 FIX: Diagnose why a product is incomplete
   * @param {Object} product - Product to check
   * @param {number} index - Product index (0-2)
   * @param {string} recoveryStep - Step to navigate to for recovery
   * @returns {Object} Diagnosis with reason and recovery step
   */
  diagnoseProductIncomplete(product, index, recoveryStep) {
    const productNum = index + 1;
    const productName = product.productTitle || `Product ${productNum}`;

    if (!product.selectedVariantId) {
      return {
        allowed: false,
        reason: `Please select a variant for ${productName}`,
        recoveryStep: recoveryStep
      };
    }

    if (product.requirements?.requiresPetName) {
      const petNames = product.customization?.petNames || [];
      const requiredCount = product.pendantCount || 1;

      for (let i = 0; i < requiredCount; i++) {
        if (!petNames[i] || petNames[i].trim() === '') {
          return {
            allowed: false,
            reason: `Please add a pet name for ${productName}`,
            recoveryStep: recoveryStep
          };
        }
      }
    }

    if (product.requirements?.requiresPhoto && !product.customization?.photoUrl) {
      return {
        allowed: false,
        reason: `Please upload a photo for ${productName}`,
        recoveryStep: recoveryStep
      };
    }

    // Fallback - shouldn't normally reach here
    return {
      allowed: false,
      reason: `Please complete customization for ${productName}`,
      recoveryStep: recoveryStep
    };
  }

  /**
   * BUG-048 FIX: Find index of first incomplete product
   * @returns {number|null} Index of first incomplete product, or null if all complete
   */
  findFirstIncompleteProduct() {
    for (let i = 0; i < this.state.products.length; i++) {
      if (!BundleStateManager.isProductComplete(this.state.products[i])) {
        return i;
      }
    }
    return null;
  }

  /**
   * Navigate to a specific step
   * @param {string} step - Target step name
   * @returns {boolean} True if navigation succeeded
   */
  goToStep(step) {
    if (!this.canNavigateTo(step)) {
      return false;
    }

    // BUG-073 FIX: Capture previous step BEFORE updating state
    const prevStep = this.state.currentStep;

    this.set({ currentStep: step });

    // Dispatch event for UI components
    window.dispatchEvent(new CustomEvent('bundle:step-changed', {
      detail: { step, prevStep, state: this.state }
    }));

    return true;
  }

  /**
   * Navigate to previous step
   * @returns {boolean} True if navigation succeeded
   */
  goBack() {
    const backStep = this.getPreviousStep();
    if (backStep) {
      // BUG-067 FIX: Clear customization state when going back to selection
      // This ensures the choice modal appears fresh when clicking "Next Step" again
      if (backStep === 'selection') {
        this.set({
          customizationMode: null,
          hasCommonVariants: false,
          commonVariants: null,
          unifiedCustomization: null
        });
      }
      return this.goToStep(backStep);
    }
    return false;
  }

  /**
   * Get the previous step based on current step and customization mode
   * @returns {string|null} Previous step name or null if at start
   */
  getPreviousStep() {
    const current = this.state.currentStep;

    // Special case: upsells goes back to last customization step
    if (current === 'upsells') {
      return this.state.customizationMode === 'together'
        ? 'together'
        : 'individual-3';
    }

    return BundleStateManager.STEP_BACK_MAP[current] || null;
  }

  /**
   * Get the next step based on current step
   * @returns {string|null} Next step name or null if at end
   */
  getNextStep() {
    const current = this.state.currentStep;
    const stepMap = {
      'selection': 'choice',
      'choice': this.state.customizationMode === 'together' ? 'together' : 'individual-1',
      'together': 'upsells',
      'individual-1': 'individual-2',
      'individual-2': 'individual-3',
      'individual-3': 'upsells',
      'upsells': 'summary',
      'summary': null
    };
    return stepMap[current] || null;
  }

  /**
   * Navigate to next step
   * @returns {boolean} True if navigation succeeded
   */
  goNext() {
    const nextStep = this.getNextStep();
    if (nextStep) {
      return this.goToStep(nextStep);
    }
    return false;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PRODUCT MANAGEMENT
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Add a product to the bundle
   * @param {Object} product - Product data
   * @returns {boolean} True if product was added
   */
  addProduct(product) {
    // Validate required fields
    if (!product.productId || !product.productHandle) {
      return false;
    }

    // Check max 3 products
    if (this.state.products.length >= 3) {
      return false;
    }

    // Create complete product entry
    const productEntry = this.createProductEntry(product);

    // Add to products array
    const products = [...this.state.products, productEntry];

    this.set({ products });

    // Dispatch event
    window.dispatchEvent(new CustomEvent('bundle:product-added', {
      detail: { product: productEntry, index: products.length - 1 }
    }));

    return true;
  }

  /**
   * Remove the first bundle entry for a product
   * @param {number} productId - Product ID to remove
   * @returns {boolean} True if product was removed
   */
  removeProduct(productId) {
    return this.removeProductByIndex(this.state.products.findIndex(p => p.productId === productId));
  }

  /**
   * Remove the product at a bundle position (the same product can fill several positions)
   * @param {number} index - Product index (0, 1, or 2)
   * @returns {boolean} True if product was removed
   */
  removeProductByIndex(index) {
    const product = this.state.products[index];

    if (!product) {
      return false;
    }

    const productId = product.productId;
    const products = this.state.products.filter((_, i) => i !== index);

    // Reset customization mode if less than 3 products
    const updates = { products };
    if (products.length < 3) {
      updates.customizationMode = null;
      updates.hasCommonVariants = false;
      updates.commonVariants = null;
      updates.unifiedCustomization = null;

      // Reset to selection step if currently on a step that requires 3 products
      if (this.state.currentStep !== 'selection') {
        updates.currentStep = 'selection';
      }
    }

    this.set(updates);

    // Dispatch step-changed event if step was reset (for UI router to update visibility)
    if (updates.currentStep === 'selection') {
      window.dispatchEvent(new CustomEvent('bundle:step-changed', {
        detail: { step: 'selection', state: this.state }
      }));
    }

    // Dispatch product-removed event
    window.dispatchEvent(new CustomEvent('bundle:product-removed', {
      detail: { productId, index }
    }));

    return true;
  }

  /**
   * Update a product in the bundle
   * @param {number} productId - Product ID to update
   * @param {Object} updates - Partial updates to merge
   * @returns {boolean} True if product was updated
   */
  updateProduct(productId, updates) {
    const index = this.state.products.findIndex(p => p.productId === productId);

    if (index === -1) {
      return false;
    }

    const products = [...this.state.products];
    products[index] = {
      ...products[index],
      ...updates
    };

    // Handle nested customization updates
    if (updates.customization) {
      products[index].customization = {
        ...products[index].customization,
        ...updates.customization
      };
    }

    this.set({ products });

    // Dispatch event
    window.dispatchEvent(new CustomEvent('bundle:product-updated', {
      detail: { productId, index, updates }
    }));

    return true;
  }

  /**
   * Update a product in the bundle by index (BUG-045 FIX: handles duplicate products)
   * When the same product is added multiple times, productId is not unique.
   * This method updates by position index instead of productId.
   * @param {number} index - Product index (0, 1, or 2)
   * @param {Object} updates - Partial updates to merge
   * @returns {boolean} True if product was updated
   */
  updateProductByIndex(index, updates) {
    if (index < 0 || index >= this.state.products.length) {
      return false;
    }

    const products = [...this.state.products];
    const productId = products[index].productId;

    products[index] = {
      ...products[index],
      ...updates
    };

    // Handle nested customization updates
    if (updates.customization) {
      products[index].customization = {
        ...products[index].customization,
        ...updates.customization
      };
    }

    this.set({ products });

    // Dispatch event
    window.dispatchEvent(new CustomEvent('bundle:product-updated', {
      detail: { productId, index, updates }
    }));

    return true;
  }

  /**
   * Get a product by ID
   * @param {number} productId - Product ID
   * @returns {Object|null} Product or null if not found
   */
  getProduct(productId) {
    return this.state.products.find(p => p.productId === productId) || null;
  }

  /**
   * Get a product by index (0-2)
   * @param {number} index - Product index
   * @returns {Object|null} Product or null if not found
   */
  getProductByIndex(index) {
    return this.state.products[index] || null;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // CUSTOMIZATION MODE
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Set customization mode
   * @param {string} mode - 'together' or 'individual'
   * @param {Array} commonVariants - Array of common variant options
   */
  setCustomizationMode(mode, commonVariants = null) {
    const hasCommonVariants = commonVariants && commonVariants.length > 0;

    // Force individual if no common variants
    const actualMode = (mode === 'together' && !hasCommonVariants)
      ? 'individual'
      : mode;

    this.set({
      customizationMode: actualMode,
      hasCommonVariants,
      commonVariants: hasCommonVariants ? commonVariants : null,
      unifiedCustomization: actualMode === 'together' ? {
        selectedOptions: {},
        photoUrl: null,
        petName: null
      } : null
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // VALIDATION HELPERS (Static)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Check if a product has all required customization complete
   * @param {Object} product - Product to check
   * @returns {boolean} True if complete
   */
  static isProductComplete(product) {
    if (!product) return false;

    // Must have variant selected
    if (!product.selectedVariantId) return false;

    // Check pet name requirement
    if (product.requirements?.requiresPetName) {
      const petNames = product.customization?.petNames || [];
      const requiredCount = product.pendantCount || 1;

      // Must have enough pet names
      if (petNames.length < requiredCount) return false;

      // All pet names must be non-empty
      for (let i = 0; i < requiredCount; i++) {
        if (!petNames[i] || petNames[i].trim() === '') return false;
      }
    }

    // Check photo requirement
    if (product.requirements?.requiresPhoto) {
      if (!product.customization?.photoUrl) return false;
    }

    return true;
  }

  /**
   * Check if all products in the bundle are complete
   * @param {Object} state - Bundle state
   * @returns {boolean} True if all complete
   */
  static areAllProductsComplete(state) {
    if (!state.products || state.products.length !== 3) return false;

    // BUG-117 FIX: Together mode stores customization in unified state
    if (state.customizationMode === 'together') {
      // Check variants for all products
      if (!state.products.every(p => p.selectedVariantId)) return false;

      // Check pet names from unified state (if any product requires it)
      const maxPendantCount = BundleStateManager.getTogetherNameCount(state.products);
      if (maxPendantCount > 0) {
        const petNames = state.unifiedCustomization?.petNames || [];
        for (let i = 0; i < maxPendantCount; i++) {
          if (!petNames[i] || petNames[i].trim() === '') return false;
        }
      }

      // Check photo from unified state (if any product requires it)
      const anyRequiresPhoto = state.products.some(p => p.requirements?.requiresPhoto);
      if (anyRequiresPhoto && !state.unifiedCustomization?.photoUrl) return false;

      return true;
    }

    // Individual mode - use existing per-product validation
    return state.products.every(p => BundleStateManager.isProductComplete(p));
  }

  /**
   * Number of shared names Together mode needs: the largest pendant count among
   * products that require a pet name (0 when none do)
   * @param {Array} products - Bundle products
   * @returns {number} Required name count
   */
  static getTogetherNameCount(products) {
    const counts = (products || [])
      .filter(p => p.requirements?.requiresPetName)
      .map(p => p.pendantCount || 1);
    return counts.length > 0 ? Math.max(...counts) : 0;
  }

  /**
   * BUG-116 FIX: Normalize material/color variant names to canonical display names
   * Handles different naming conventions across products:
   * - "Sterling Silver", "Silver 925", "S925", "Stainless Steel" → "Silver"
   * - "18K Gold", "14K Gold Plated", "Gold" → "Gold"
   * - "18K Rose Gold", "Rose Gold Plated" → "Rose Gold"
   *
   * @param {string} value - Raw variant value from Shopify
   * @returns {string} Normalized canonical name for display
   */
  static normalizeMaterialValue(value) {
    if (!value) return value;

    const lower = value.toLowerCase().trim();

    // Rose Gold family - must check BEFORE gold (since "rose gold" contains "gold")
    if (lower.includes('rose gold') || lower.includes('rosegold')) {
      return 'Rose Gold';
    }

    // Silver family - includes stainless steel (silver-colored)
    if (
      lower.includes('silver') ||
      lower.includes('sterling') ||
      lower.includes('stainless') ||
      lower.includes('925') ||
      lower.includes('s925')
    ) {
      return 'Silver';
    }

    // Gold family (check after rose gold is filtered out)
    if (lower.includes('gold')) {
      return 'Gold';
    }

    // Platinum family
    if (lower.includes('platinum')) {
      return 'Platinum';
    }

    // No normalization needed - return original value
    return value;
  }

  /**
   * Find common variant options across all products in bundle
   * BUG-116 FIX: Uses material normalization for fuzzy/semantic matching
   * Used to determine if "Customize Together" mode is available
   *
   * @param {Array} productsData - Array of full product JSON from Shopify API
   * @returns {Array} Common options with value mappings:
   *   [{
   *     name: 'Material',
   *     values: ['Silver', 'Gold', 'Rose Gold'],  // Normalized display names
   *     valueMapping: {
   *       'Silver': ['Sterling Silver', 'Silver 925', 'Silver'],  // Per-product actual names
   *       'Gold': ['18K Gold', '14K Gold Plated', 'Gold'],
   *       'Rose Gold': ['Rose Gold', 'Rose Gold', '18K Rose Gold']
   *     }
   *   }]
   */
  static findCommonVariants(productsData) {
    if (!productsData || productsData.length === 0) return [];

    // Get option names and values from each product WITH normalization
    const productOptions = productsData.map((product, productIndex) => {
      const options = {};

      // BUG-018 FIX: Handle both string and object formats for product.options
      product.options.forEach((option, idx) => {
        const optionName = typeof option === 'string' ? option : option.name;
        const optionKey = optionName.toLowerCase();

        // BUG-098 FIX: Extract values with null/empty filtering
        const rawValues = product.variants
          .filter(v => v.available)
          .map(v => v[`option${idx + 1}`])
          .filter(v => v != null && v !== '');

        // BUG-116 FIX: Create normalized → original mapping
        // Key: normalized lowercase, Value: { display: 'Silver', originals: ['Sterling Silver'] }
        const normalizedMap = {};

        rawValues.forEach(originalValue => {
          const normalized = BundleStateManager.normalizeMaterialValue(originalValue);
          const normalizedLower = normalized.toLowerCase();

          if (!normalizedMap[normalizedLower]) {
            normalizedMap[normalizedLower] = {
              display: normalized,           // Display name (e.g., "Silver")
              originals: [originalValue]     // Product-specific names
            };
          } else {
            // Add to existing normalized group (handle multiple originals mapping to same normalized)
            if (!normalizedMap[normalizedLower].originals.includes(originalValue)) {
              normalizedMap[normalizedLower].originals.push(originalValue);
            }
          }
        });

        options[optionKey] = {
          values: Object.keys(normalizedMap),  // Normalized lowercase for intersection
          normalizedMap: normalizedMap          // For mapping back to originals
        };
      });

      return options;
    });

    // Find intersection of option names (options that exist in ALL products)
    const firstProductOptionNames = Object.keys(productOptions[0]);
    const commonOptionNames = firstProductOptionNames.filter(name =>
      productOptions.every(opts => name in opts && opts[name].values?.length > 0)
    );

    // For each common option, find intersection of NORMALIZED values
    const commonOptions = [];

    for (const name of commonOptionNames) {
      const allValuesData = productOptions.map(opts => opts[name]);

      // Intersection on normalized lowercase values
      const commonNormalized = allValuesData.reduce((common, data) =>
        common.filter(v => data.values.includes(v))
      , allValuesData[0].values);

      if (commonNormalized.length === 0) continue;

      // Build display values and per-product mapping
      const displayValues = [];
      const valueMapping = {};

      commonNormalized.forEach(normalizedLower => {
        // Get display name from first product
        const displayName = allValuesData[0].normalizedMap[normalizedLower].display;
        displayValues.push(displayName);

        // BUG-116 FIX: Build mapping: displayName → [product0Original, product1Original, product2Original]
        valueMapping[displayName] = allValuesData.map(data => {
          const originals = data.normalizedMap[normalizedLower]?.originals || [];
          // Return first available original value for this product
          return originals[0] || displayName;
        });
      });

      if (displayValues.length > 0) {
        commonOptions.push({
          name: name.charAt(0).toUpperCase() + name.slice(1),  // Title case
          values: displayValues,
          valueMapping: valueMapping  // BUG-116: Maps display → actual per product
        });
      }
    }

    return commonOptions;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PRODUCT REQUIREMENTS PARSING (Static)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * BUG-069 FIX: Parse product tags to extract requirements
   * Determines photo upload, pet name, character limit, NoCaps, and pendant count
   * from product tags and variant options.
   *
   * @param {Object} productData - Full product JSON from Shopify API
   * @param {Object|null} variant - Selected variant (for pendant count extraction)
   * @returns {Object} { charLimit, requiresPhoto, requiresPetName, noCaps, pendantCount }
   *
   * Tag System:
   * - photo-upload: Product requires photo upload
   * - pet-name: Product requires pet name input(s)
   * - Xchar: Character limit (1char, 3char, 6char, 8char, 12char, 16char, 24char)
   * - NoCaps: Use Title Case instead of ALL CAPS (case-sensitive tag)
   * - Pendant count: From variant option containing "pendant" (e.g., "2 Pendants" → 2)
   */
  static parseProductRequirements(productData, variant = null) {
    const tags = productData?.tags || [];

    // Handle both array and comma-separated string formats from Shopify API
    const tagsArray = Array.isArray(tags)
      ? tags.map(t => t.toLowerCase().trim())
      : (typeof tags === 'string' ? tags.split(',').map(t => t.toLowerCase().trim()) : []);

    // Photo upload required - product has "photo-upload" tag
    const requiresPhoto = tagsArray.includes('photo-upload');

    // Pet name required - product has "pet-name" tag
    const requiresPetName = tagsArray.includes('pet-name');

    // Character limit (1char, 3char, 6char, 8char, 12char, 16char, 24char)
    // Default to 8 if no character limit tag found
    let charLimit = 8;
    const charLimitTags = [1, 3, 6, 8, 12, 16, 24];
    for (const limit of charLimitTags) {
      if (tagsArray.includes(`${limit}char`)) {
        charLimit = limit;
        break;
      }
    }

    // NoCaps check - use Title Case instead of ALL CAPS
    // BUG-069 HARDENING: Made case-insensitive so 'nocaps', 'NoCaps', 'NOCAPS' all work
    const noCaps = tagsArray.includes('nocaps');

    // Pendant count from variant options (e.g., "2 Pendants" → 2)
    let pendantCount = 1;
    if (variant && productData?.options) {
      for (let i = 0; i < productData.options.length; i++) {
        const opt = productData.options[i];
        // Handle both string and object option formats from Shopify
        const optionName = (typeof opt === 'string' ? opt : opt.name || '').toLowerCase();
        if (optionName.includes('pendant')) {
          // variant.option1, variant.option2, etc. contain the selected value;
          // skip "pendant" options without a count (e.g. "Pendant Shape")
          const optionValue = variant[`option${i + 1}`];
          const value = parseInt(optionValue, 10);
          if (value >= 1 && value <= 4) {
            pendantCount = value;
            break;
          }
        }
      }
    }

    return { charLimit, requiresPhoto, requiresPetName, noCaps, pendantCount };
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // NETWORK UTILITIES (Static)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Fetch with timeout wrapper - prevents indefinite hangs
   * Uses AbortController for clean cancellation
   *
   * @param {string} url - URL to fetch
   * @param {Object} options - Fetch options (method, headers, body, etc.)
   * @param {number} timeout - Timeout in milliseconds (default: 15000ms)
   * @returns {Promise<Response>} The fetch response
   * @throws {Error} TIMEOUT error if request times out, or original fetch error
   *
   * Why this solves the problem:
   * - Native fetch has no timeout (browser default ~90s)
   * - Users wait forever on slow networks
   * - AbortController cleanly cancels in-flight requests
   * - 15s default balances user experience with slow connections
   */
  static async fetchWithTimeout(url, options = {}, timeout = 15000) {
    // Create abort controller for cancellation
    const controller = new AbortController();
    const { signal } = controller;

    // Start timeout timer
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, timeout);

    try {
      // Merge signal into options
      const response = await fetch(url, {
        ...options,
        signal
      });

      // Clear timeout on successful response
      clearTimeout(timeoutId);
      return response;

    } catch (error) {
      // Clear timeout on error
      clearTimeout(timeoutId);

      // Transform abort error to identifiable TIMEOUT error
      if (error.name === 'AbortError') {
        const timeoutError = new Error('NETWORK_TIMEOUT');
        timeoutError.name = 'TimeoutError';
        timeoutError.originalError = error;
        throw timeoutError;
      }

      // Re-throw original error (network failure, etc.)
      throw error;
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PERSISTENCE
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Save state to localStorage with debounce
   */
  debouncedSave() {
    clearTimeout(this.saveTimer);

    this.saveTimer = setTimeout(() => {
      try {
        localStorage.setItem(
          BundleStateManager.STORAGE_KEY,
          JSON.stringify(this.state)
        );
      } catch (e) {
        // localStorage quota exceeded or disabled - continue without persistence
        // State still works in memory for current session
      }
    }, BundleStateManager.SAVE_DELAY);
  }

  /**
   * Immediately flush any pending writes (called on page unload)
   */
  flushPendingWrites() {
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;

      try {
        localStorage.setItem(
          BundleStateManager.STORAGE_KEY,
          JSON.stringify(this.state)
        );
      } catch (e) {
        // Ignore errors during flush
      }
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // CLEANUP (Theme Editor Support)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Clean up all timers and listeners
   */
  cleanup() {
    // Clear debounce timer
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
    }

    // Clear all listeners
    this.listeners.clear();

    // Remove event handlers
    if (this.boundFlush) {
      window.removeEventListener('beforeunload', this.boundFlush);
      this.boundFlush = null;
    }

    if (this.boundVisibilityChange) {
      document.removeEventListener('visibilitychange', this.boundVisibilityChange);
      this.boundVisibilityChange = null;
    }
  }

  /**
   * Reinitialize for theme editor section reload
   */
  reinit() {
    this.cleanup();
    this.isHydrated = false;
    setTimeout(() => this.init(), 100);
  }

  /**
   * Clear all state (call after successful checkout)
   */
  clear() {
    try {
      localStorage.removeItem(BundleStateManager.STORAGE_KEY);
    } catch (e) {
      // Ignore errors
    }

    this.state = this.getInitialState();
    this.notifyListeners();

    // Dispatch event
    window.dispatchEvent(new CustomEvent('bundle:cleared'));
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // DEBUG UTILITIES
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Get debug information about current state
   * @returns {Object} Debug info
   */
  debug() {
    const age = this.state
      ? Math.round((Date.now() - this.state.updatedAt) / 1000 / 60)
      : 0;

    return {
      step: this.state?.currentStep || 'none',
      products: this.state?.products?.length || 0,
      mode: this.state?.customizationMode || 'none',
      complete: this.state ? BundleStateManager.areAllProductsComplete(this.state) : false,
      ageMinutes: age,
      listeners: this.listeners.size,
      isHydrated: this.isHydrated,
      hasPendingWrite: this.saveTimer !== null
    };
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// INITIALIZATION
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Initialize bundle state manager when DOM is ready
 * This function is called first, then chains to UI initialization
 */
const initBundleStateManager = async () => {
  const manager = BundleStateManager.getInstance();
  await manager.init();

  // Expose globally for console access and other modules
  window.bundleState = manager;

  // Listen for theme editor section events
  document.addEventListener('shopify:section:load', (e) => {
    // Reinit if bundle section was loaded
    if (e.target.querySelector('[data-bundle-builder]')) {
      manager.reinit();
    }
  });

  document.addEventListener('shopify:section:unload', (e) => {
    // Cleanup if bundle section was unloaded
    if (e.target.querySelector('[data-bundle-builder]')) {
      manager.cleanup();
    }
  });
};

/**
 * Resolve once DOMContentLoaded has fired. The bundle modules are deferred scripts, which
 * all run before DOMContentLoaded, so every module (UI, quick view, router, steps) exists then.
 * Window load (always after DOMContentLoaded) is a fallback when Navigation Timing is missing.
 * @returns {Promise<void>}
 */
const whenBundleModulesLoaded = () => new Promise((resolve) => {
  const navigation = typeof performance !== 'undefined' && performance.getEntriesByType
    ? performance.getEntriesByType('navigation')[0]
    : null;
  const domContentLoaded = document.readyState === 'complete' ||
    (document.readyState === 'interactive' && navigation?.domContentLoadedEventStart > 0);

  if (domContentLoaded) {
    resolve();
  } else {
    document.addEventListener('DOMContentLoaded', () => resolve(), { once: true });
    window.addEventListener('load', () => resolve(), { once: true });
  }
});

// Run initialization - hydrate state now, start the UI once all bundle modules have loaded
(async () => {
  await Promise.all([initBundleStateManager(), whenBundleModulesLoaded()]);
  // UI initialization is in custom-bundle-ui.js
  if (typeof initBundleUIManager === 'function') {
    await initBundleUIManager();
  }
})();

// Export class for other modules
window.BundleStateManager = BundleStateManager;
